<template>
  <div class="max-w-4xl mx-auto">
    <div class="bg-white rounded-xl shadow-md overflow-hidden">
      <div class="p-6 border-b border-gray-200 bg-gray-50">
        <h1 class="text-2xl font-bold text-gray-800">Submit Research Article</h1>
        <p class="text-gray-600 mt-2">Share your research with the global scientific community</p>
      </div>

      <form @submit.prevent="submitForm" class="p-6 space-y-6">
        <!-- Title -->
        <div>
          <label for="title" class="block text-sm font-medium text-gray-700 mb-2">Article Title *</label>
          <input 
            type="text"
            id="title"
            v-model="form.title"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            placeholder="Enter the title of your research article"
          >
        </div>

        <!-- Authors -->
        <div>
          <label for="authors" class="block text-sm font-medium text-gray-700 mb-2">Authors *</label>
          <div class="space-y-2">
            <div v-for="(author, index) in form.authors" :key="index" class="flex gap-2">
              <input 
                type="text"
                v-model="form.authors[index]"
                required
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder="Author name"
              >
              <button 
                v-if="form.authors.length > 1"
                type="button"
                @click="removeAuthor(index)"
                class="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <button 
              type="button"
              @click="addAuthor"
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              <i class="fas fa-plus mr-2"></i> Add Author
            </button>
          </div>
        </div>

        <!-- Abstract -->
        <div>
          <label for="abstract" class="block text-sm font-medium text-gray-700 mb-2">Abstract *</label>
          <textarea 
            id="abstract"
            v-model="form.abstract"
            required
            rows="6"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            placeholder="Provide a comprehensive abstract of your research"
          ></textarea>
        </div>

        <!-- Category -->
        <div>
          <label for="category" class="block text-sm font-medium text-gray-700 mb-2">Category *</label>
          <select 
            id="category"
            v-model="form.category"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Select a category</option>
            <option value="Computer Science">Computer Science</option>
            <option value="Artificial Intelligence">Artificial Intelligence</option>
            <option value="Environmental Science">Environmental Science</option>
            <option value="Psychology">Psychology</option>
            <option value="Bioethics">Bioethics</option>
            <option value="Information Technology">Information Technology</option>
            <option value="Neuroscience">Neuroscience</option>
            <option value="Education">Education</option>
          </select>
        </div>

        <!-- Document Type -->
        <div>
          <label for="type" class="block text-sm font-medium text-gray-700 mb-2">Document Type *</label>
          <select 
            id="type"
            v-model="form.type"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Select document type</option>
            <option value="Research Article">Research Article</option>
            <option value="Review Article">Review Article</option>
            <option value="Meta-Analysis">Meta-Analysis</option>
            <option value="Perspective">Perspective</option>
            <option value="Case Study">Case Study</option>
          </select>
        </div>

        <!-- Tags -->
        <div>
          <label for="tags" class="block text-sm font-medium text-gray-700 mb-2">Keywords/Tags *</label>
          <div class="space-y-2">
            <div class="flex flex-wrap gap-2 mb-2">
              <span 
                v-for="(tag, index) in form.tags"
                :key="index"
                class="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
              >
                {{ tag }}
                <button 
                  type="button"
                  @click="removeTag(index)"
                  class="ml-2 text-green-600 hover:text-green-800"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
            </div>
            <div class="flex gap-2">
              <input 
                type="text"
                v-model="newTag"
                @keyup.enter="addTag"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                placeholder="Enter a keyword and press Enter"
              >
              <button 
                type="button"
                @click="addTag"
                class="px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <!-- Journal -->
        <div>
          <label for="journal" class="block text-sm font-medium text-gray-700 mb-2">Target Journal</label>
          <input 
            type="text"
            id="journal"
            v-model="form.journal"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
            placeholder="Enter target journal name (optional)"
          >
        </div>

        <!-- Open Access -->
        <div>
          <div class="flex items-center">
            <input 
              type="checkbox"
              id="openAccess"
              v-model="form.openAccess"
              class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            >
            <label for="openAccess" class="ml-2 text-sm text-gray-700">
              Make this article Open Access
            </label>
          </div>
        </div>

        <!-- Main Manuscript Upload -->
        <div>
          <label for="main-manuscript" class="block text-sm font-medium text-gray-700 mb-2">Upload Main Manuscript *</label>
          <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div class="space-y-1 text-center">
              <div v-if="!selectedMainFile">
                <i class="fas fa-file-upload text-4xl text-gray-400"></i>
                <div class="flex text-sm text-gray-600">
                  <label for="main-file-upload" class="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <span>Upload main manuscript</span>
                    <input 
                      id="main-file-upload"
                      name="main-file-upload"
                      type="file"
                      class="sr-only"
                      accept=".pdf,.doc,.docx"
                      @change="handleMainFileUpload"
                    >
                  </label>
                  <p class="pl-1">or drag and drop</p>
                </div>
                <p class="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
              </div>
              <div v-else class="text-left">
                <div class="flex items-center">
                  <i class="fas fa-file-pdf text-red-500 text-xl mr-2" v-if="selectedMainFile.type === 'application/pdf'"></i>
                  <i class="fas fa-file-word text-blue-500 text-xl mr-2" v-else></i>
                  <span class="text-sm text-gray-700">{{ selectedMainFile.name }}</span>
                  <button 
                    type="button"
                    @click="removeMainFile"
                    class="ml-auto text-sm text-red-600 hover:text-red-800"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div class="mt-2">
                  <div class="bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      class="bg-indigo-600 h-2 rounded-full"
                      :style="{width: mainUploadProgress + '%'}"
                    ></div>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ mainUploadProgress < 100 ? `Uploading: ${mainUploadProgress}%` : 'Upload complete' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Blinded Manuscript Upload -->
        <div>
          <label for="blinded-manuscript" class="block text-sm font-medium text-gray-700 mb-2">Upload Blinded Manuscript</label>
          <p class="text-xs text-gray-500 mb-2">Optional: Upload a version with author information removed for blind peer review</p>
          <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div class="space-y-1 text-center">
              <div v-if="!selectedBlindedFile">
                <i class="fas fa-file-upload text-4xl text-gray-400"></i>
                <div class="flex text-sm text-gray-600">
                  <label for="blinded-file-upload" class="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <span>Upload blinded manuscript</span>
                    <input 
                      id="blinded-file-upload"
                      name="blinded-file-upload"
                      type="file"
                      class="sr-only"
                      accept=".pdf,.doc,.docx"
                      @change="handleBlindedFileUpload"
                    >
                  </label>
                  <p class="pl-1">or drag and drop</p>
                </div>
                <p class="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
              </div>
              <div v-else class="text-left">
                <div class="flex items-center">
                  <i class="fas fa-file-pdf text-red-500 text-xl mr-2" v-if="selectedBlindedFile.type === 'application/pdf'"></i>
                  <i class="fas fa-file-word text-blue-500 text-xl mr-2" v-else></i>
                  <span class="text-sm text-gray-700">{{ selectedBlindedFile.name }}</span>
                  <button 
                    type="button"
                    @click="removeBlindedFile"
                    class="ml-auto text-sm text-red-600 hover:text-red-800"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <div class="mt-2">
                  <div class="bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      class="bg-indigo-600 h-2 rounded-full"
                      :style="{width: blindedUploadProgress + '%'}"
                    ></div>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ blindedUploadProgress < 100 ? `Uploading: ${blindedUploadProgress}%` : 'Upload complete' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end space-x-4">
          <button 
            type="button"
            class="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Save as Draft
          </button>
          <button 
            type="submit"
            :disabled="!isLoggedIn"
            class="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Submit Article
          </button>
        </div>
        <div v-if="!isLoggedIn" class="text-center text-sm text-gray-500">
          <i class="fas fa-info-circle mr-1"></i>
          Please sign in to submit your research article
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'SubmitView',
  props: {
    isLoggedIn: Boolean
  },
  emits: ['submit-article'],
  setup(props, { emit }) {
    const form = ref({
      title: '',
      authors: [''],
      abstract: '',
      category: '',
      type: '',
      tags: [],
      journal: '',
      openAccess: false,
      documentUrl: [],
      blindedManuscriptUrl: [],
      documentName: '',
      blindedDocumentName: ''
    })

    const newTag = ref('')
    
    // Main manuscript upload variables
    const selectedMainFile = ref(null)
    const mainUploadProgress = ref(0)
    
    // Blinded manuscript upload variables
    const selectedBlindedFile = ref(null)
    const blindedUploadProgress = ref(0)

    const generateSlug = (title) => {
      const base = title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')     // remove special characters
        .replace(/\s+/g, '-')            // replace spaces with -
        .trim()
      
      // Add a 6-char random suffix to prevent duplicate slugs
      const randomSuffix = Math.random().toString(36).substring(2, 8)
      return `${base}-${randomSuffix}`
    }

    const handleMainFileUpload = (event) => {
      const file = event.target.files[0]
      if (!file) return

      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit')
        return
      }

      selectedMainFile.value = file
      mainUploadProgress.value = 0

      if (props.isLoggedIn) {
        uploadToPhpServer(file, 'main')
      } else {
        alert('Please sign in to upload files')
      }
    }

    const handleBlindedFileUpload = (event) => {
      const file = event.target.files[0]
      if (!file) return

      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit')
        return
      }

      selectedBlindedFile.value = file
      blindedUploadProgress.value = 0

      if (props.isLoggedIn) {
        uploadToPhpServer(file, 'blinded')
      } else {
        alert('Please sign in to upload files')
      }
    }

    const uploadToPhpServer = async (file, type) => {
      const formData = new FormData()
      formData.append('file', file)
      
      try {
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          if (type === 'main') {
            mainUploadProgress.value = Math.min(mainUploadProgress.value + 10, 90)
          } else {
            blindedUploadProgress.value = Math.min(blindedUploadProgress.value + 10, 90)
          }
        }, 100)

        const response = await fetch('https://daluyangdunong.minsu.edu.ph/api/upload.php', {
          method: 'POST',
          body: formData
        })
        
        clearInterval(progressInterval)
        const result = await response.json()

        if (result.success && result.url) {
          const uploadedAt = new Date().toLocaleString('en-PH', {
            timeZone: 'Asia/Manila',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true
          })

          // Store file info based on type
          if (type === 'main') {
            form.value.documentUrl.push({
              url: result.url,
              name: file.name,
              uploadedAt
            })
            form.value.documentName = file.name
            mainUploadProgress.value = 100
          } else {
            form.value.blindedManuscriptUrl.push({
              url: result.url,
              name: file.name,
              uploadedAt
            })
            form.value.blindedDocumentName = file.name
            blindedUploadProgress.value = 100
          }

          console.log(`${type} file uploaded:`, result.url, 'at', uploadedAt)
        } else {
          alert('File upload failed: ' + (result.error || 'Unknown error'))
          if (type === 'main') {
            selectedMainFile.value = null
            mainUploadProgress.value = 0
          } else {
            selectedBlindedFile.value = null
            blindedUploadProgress.value = 0
          }
        }
      } catch (error) {
        console.error('Upload error:', error)
        alert('Error uploading file: ' + error.message)
        if (type === 'main') {
          selectedMainFile.value = null
          mainUploadProgress.value = 0
        } else {
          selectedBlindedFile.value = null
          blindedUploadProgress.value = 0
        }
      }
    }

    const removeMainFile = () => {
      selectedMainFile.value = null
      mainUploadProgress.value = 0
      form.value.documentUrl = []
      form.value.documentName = ''
    }

    const removeBlindedFile = () => {
      selectedBlindedFile.value = null
      blindedUploadProgress.value = 0
      form.value.blindedManuscriptUrl = []
      form.value.blindedDocumentName = ''
    }

    const addAuthor = () => {
      form.value.authors.push('')
    }

    const removeAuthor = (index) => {
      form.value.authors.splice(index, 1)
    }

    const addTag = () => {
      if (newTag.value.trim()) {
        form.value.tags.push(newTag.value.trim())
        newTag.value = ''
      }
    }

    const removeTag = (index) => {
      form.value.tags.splice(index, 1)
    }

    const submitForm = () => {
      if (!props.isLoggedIn) {
        alert('Please sign in to submit your article')
        return
      }

      // Required field checks
      if (!form.value.title || !form.value.abstract || !form.value.category || !form.value.type) {
        alert('Please fill in all required fields')
        return
      }

      if (form.value.authors.some(author => !author.trim())) {
        alert('Please provide all author names')
        return
      }

      if (form.value.tags.length === 0) {
        alert('Please add at least one keyword/tag')
        return
      }

      if (!form.value.documentUrl.length) {
        alert('Please upload your main manuscript before submitting.')
        return
      }

      const cleanedAuthors = form.value.authors.filter(author => author.trim())
      const articleData = {
        ...form.value,
        authors: cleanedAuthors,
        status: 'pending',
        submittedAt: new Date().toISOString(),
        slug: generateSlug(form.value.title)
      }

      emit('submit-article', articleData)

      // Reset form
      form.value = {
        title: '',
        authors: [''],
        abstract: '',
        category: '',
        type: '',
        tags: [],
        journal: '',
        openAccess: false,
        documentUrl: [],
        blindedManuscriptUrl: [],
        documentName: '',
        blindedDocumentName: ''
      }
      
      selectedMainFile.value = null
      selectedBlindedFile.value = null
      mainUploadProgress.value = 0
      blindedUploadProgress.value = 0
    }

    return {
      form,
      newTag,
      selectedMainFile,
      selectedBlindedFile,
      mainUploadProgress,
      blindedUploadProgress,
      handleMainFileUpload,
      handleBlindedFileUpload,
      removeMainFile,
      removeBlindedFile,
      submitForm,
      addAuthor,
      removeAuthor,
      addTag,
      removeTag
    }
  }
}
</script>
